import { useEffect, useState, useMemo, useRef, BaseSyntheticEvent } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { Combobox } from '@headlessui/react'
import {
  CheckIcon,
  ChevronUpDownIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/20/solid'
import { LockClosedIcon } from '@heroicons/react/24/outline'

import { useAppSelector } from '@/store/hooks'

import apiQuery from '../../api/APIServices'
import useServer from '../../api/useServerMain'
import { BasicType, SelectInterface } from '../../interfaces/InterfacesLocal'
import { generateUrlParams } from '../../utils/helpers'
import LoadingSpin from '../misc/LoadingSpin'

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

interface InputProps {
  label?: string
  className?: string
  disabled?: boolean
  dataQuery: {
    url: string
    defaultParams?: BasicType
    searchCriteria?: string
  }
  nullOpt?: { id: string | number | null; name: string }
  normalizeData: {
    id: string
    name: string | string[]
    disabled?: (string | number)[]
    format?: string
  }
  undefinedFormat?: string
  defaultItem?: { id: string | number | null; name: string }
  dependendValue?: BasicType
  callback?: (_e: SelectInterface) => void
  placeholder?: string
  nameTransformers?: ((_name: string) => string)[]
}

export default function AsyncComboBox(props: UseControllerProps & InputProps) {
  const { manageErrors } = useServer()
  const { field, fieldState } = useController(props)
  const {
    label,
    className,
    disabled,
    defaultItem,
    dataQuery,
    normalizeData,
    dependendValue,
    callback,
    nullOpt,
    undefinedFormat,
    placeholder,
    nameTransformers = [],
  } = props
  useEffect(() => {
    if (field.value === undefined) setSelectedData(null)
  }, [field.value])
  //query management states ----------------------------------------------------------
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPage] = useState(1)
  const [query, setQuery] = useState<string | null>(null)
  const [data, setData] = useState<SelectInterface[]>(
    defaultItem ? [defaultItem] : []
  )
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const [selectedData, setSelectedData] = useState<SelectInterface | null>(null)
  const [loading, setLoading] = useState(false)
  const { business } = useAppSelector((state) => state.init)

  const currentDependendValue = useRef<
    string | number | boolean | null | undefined
  >()

  useEffect(() => {
    if (defaultItem) field.onChange(defaultItem.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!field.value) setSelectedData(null)
  }, [field])

  const allParams: BasicType | undefined = useMemo(() => {
    let params: BasicType = {}
    if (dataQuery.defaultParams) {
      params = { ...dataQuery.defaultParams }
    }
    if (page > 1) {
      params = { ...dataQuery.defaultParams, page: page }
    }
    if (dependendValue) {
      const [key, value] = Object.entries(dependendValue)[0]
      if (value !== currentDependendValue.current) {
        currentDependendValue.current = value
      }
      if (currentDependendValue.current != null)
        params[key] = currentDependendValue.current
      else params[key] = '-1'
    }
    if (query) {
      if (dataQuery.searchCriteria) {
        params[dataQuery.searchCriteria] = query
      } else {
        params.search = query
      }
    }

    return params
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependendValue, query, page])

  //------------------------------------------------------------------------------------

  //API query --------------------------------------------------------------------------
  const apiCall = async (params: BasicType) => {
    setLoading(true)
    await apiQuery
      .get(`${dataQuery.url}${generateUrlParams(params)}`)
      .then((resp) => {
        const dataTotal = resp.data?.totalPages
          ? resp.data.totalPages
          : resp.data.length
        setTotalPage(dataTotal)
        const dataResp = resp.data?.items ? resp.data.items : resp.data
        const items: SelectInterface[] = dataResp?.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (elem: Record<string, string | number | boolean | null | any>) => {
            if (typeof normalizeData.name === 'string') {
              return {
                id: elem[normalizeData.id],
                name:
                  elem.business?.id === business?.id
                    ? elem[normalizeData.name]
                    : `${elem[normalizeData.name]} ${elem.business?.name ? `(${elem.business?.name})` : ''}`,
              }
            } else {
              if (normalizeData.format) {
                const str = normalizeData.format
                const keys = normalizeData.name
                let name = str
                for (const key of keys) {
                  const value = elem[key]
                    ? (elem[key] as string)
                    : undefinedFormat || '-'
                  name = name.replace(key, value)
                }
                // Apply name transformers
                //Todo : mejorar
                if (nameTransformers.length !== 0) {
                  name = str
                  for (const idx in keys) {
                    const value = elem[keys[idx]]
                      ? nameTransformers[idx] &&
                        nameTransformers[idx](elem[keys[idx]] as string)
                      : undefinedFormat || '-'
                    name = name.replace(keys[idx], value)
                  }
                }

                return {
                  id: elem[normalizeData.id],
                  name,
                }
              }
              const key = normalizeData.name.find((key) => !!elem[key])
              return {
                id: elem[normalizeData.id],
                name: key ? elem[key] : '-',
              }
            }
          }
        )
        const finalItems = nullOpt ? [nullOpt, ...items] : items
        setData(finalItems)
        if (callback) callback(resp.data.items)
      })
      .catch((e) => manageErrors(e))
    setLoading(false)
  }

  useEffect(() => {
    const elemDefault = data.find(
      (item) => item.id === defaultItem?.id || item.id === field.value
    )
    if (elemDefault) {
      setSelectedData(elemDefault)
    } else {
      setSelectedData(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultItem, data])

  useEffect(() => {
    if (
      Object.values(allParams).length !== 0 &&
      !dataQuery.url.includes('undefined')
    ) {
      apiCall(allParams)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allParams, page])

  const validateData = (value: SelectInterface) => {
    if (!value && page > 1) {
      setPage(1)
    }
  }

  //-------------------------------------------------------------------------

  //Debounce for filter -----------------------------------------------------------------------------
  const [timeOutId, setTimeOutId] = useState<number | undefined>()
  const onKeyDown = () => {
    clearTimeout(timeOutId)
  }

  const onKeyUp = (e: BaseSyntheticEvent) => {
    const time = Number(
      setTimeout(() => {
        if (e.target.value !== '') {
          setQuery(e.target.value)
        } else {
          setQuery(null)
        }
      }, 800)
    )
    setTimeOutId(Number(time))
  }
  //--------------------------------------------------------------------------------

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleScroll = (evt: any) => {
    const { scrollTop, scrollHeight, clientHeight } = evt.target

    if (
      scrollHeight - scrollTop <= clientHeight + 10 &&
      !loading &&
      page < totalPages
    ) {
      setPage((prev) => prev + 1)
    }

    if (scrollTop <= 10 && page > 1 && scrollHeight > clientHeight * 2) {
      setPage((prev) => prev - 1)
    }
  }

  const handleClose = () => {
    setPage(1)
  }

  return (
    <div className={className ? className : ''}>
      <Combobox
        as="div"
        by={(current, rest) => current?.id === rest?.id}
        disabled={disabled}
        onChange={(e: SelectInterface) => {
          setSelectedData(e)
          field.onChange(e?.id)
          validateData(e)
          if (callback) callback(e)
        }}
        value={data.find((item) => item.id === field.value) ?? null}
      >
        {label && (
          <Combobox.Label
            className={`mb-1 block text-sm font-medium ${
              disabled ? 'text-gray-400' : 'text-gray-700'
            } first-letter:uppercase`}
          >
            <span className="inline-flex items-center">
              {label}
              {disabled && <LockClosedIcon className="h-4 px-2" />}
            </span>
          </Combobox.Label>
        )}
        <div className="relative">
          <Combobox.Input
            className={`${
              fieldState.error
                ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
                : `focus:ring-gray-500 ${
                    disabled ? 'border-gray-300' : 'border-gray-400'
                  } text-gray-500 focus:border-gray-500`
            } relative w-full rounded-md border bg-white py-2 pl-3 pr-10 text-left shadow-sm sm:text-sm`}
            displayValue={(person: SelectInterface) => person?.name}
            onKeyDownCapture={onKeyDown}
            onKeyUp={onKeyUp}
            placeholder={placeholder ? placeholder : 'Criterio de búsqueda'}
          />
          <div className="absolute right-0 top-0 flex items-center gap-1 rounded-r-md p-2 px-2 focus:outline-none">
            {loading && <LoadingSpin color="gray-700" />}
            <Combobox.Button>
              <ChevronUpDownIcon
                aria-hidden="true"
                className="h-5 w-5 text-gray-400"
              />
            </Combobox.Button>
          </div>

          {data.length > 0 && (
            <Combobox.Options
              className={
                `absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base` +
                ` shadow-lg ring-1 ring-black ring-opacity-5 scrollbar-thin scrollbar-thumb-gray-400` +
                ` focus:outline-none sm:text-sm`
              }
              onBlur={handleClose}
              onScroll={(evt) => handleScroll(evt)}
            >
              {data.map((person) => (
                <Combobox.Option
                  className={({ active, disabled }) =>
                    classNames(
                      'relative cursor-default select-none py-2 pl-3 pr-9',
                      active
                        ? 'bg-slate-600 text-white'
                        : disabled
                          ? 'bg-white text-gray-400'
                          : 'text-gray-900'
                    )
                  }
                  disabled={normalizeData?.disabled?.some(
                    (item) => item === person.id
                  )}
                  key={person.id}
                  value={person}
                >
                  {({ active, selected }) => (
                    <>
                      <span
                        className={classNames(
                          'block truncate',
                          `${selected ? 'font-semibold' : ''}`
                        )}
                      >
                        {person.name}
                      </span>

                      {selected && (
                        <span
                          className={classNames(
                            'absolute inset-y-0 right-0 flex items-center pr-4',
                            active ? 'text-white' : 'text-indigo-600'
                          )}
                        >
                          <CheckIcon aria-hidden="true" className="h-5 w-5" />
                        </span>
                      )}
                    </>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          )}
          {fieldState.error && (
            <>
              <ExclamationCircleIcon
                aria-hidden="true"
                className="absolute right-8 top-2 h-5 w-5 text-red-500"
              />
              <span className="absolute left-0 top-10 text-xs text-red-600">
                {fieldState.error.message}
              </span>
            </>
          )}
        </div>
      </Combobox>
    </div>
  )
}
